import { useMemo, useState } from 'react';
import './authn-layout.less';

import type { NOTIF_BANNER_TYPE } from '@imprivata-cloud/components';
import { useEventListener } from '@imprivata-cloud/core';
import {
  NotificationSystem,
  notificationFromAppEvent,
  useNotificationChannel,
} from '@imprivata-cloud/design-system';
import { useTranslation } from 'react-i18next';

import { offlineBannerMetaData } from '@/utils/bannerUtils';

import ImprivataLogo from '../../assets/Imprivata_Logo.svg?react';
import { AuthenticationHeader } from './components/authentication-header/authentication-header';
import { EnrollmentHeader } from './components/enrollment-header/enrollment-header';
export enum LayoutHeaders {
  AUTHENTICATION = 'AUTHENTICATION',
  ENROLL_CONSENT = 'ENROLL_CONSENT',
}

interface AuthnLayoutProps {
  children: React.ReactNode;
  logoUrl?: string | React.ReactNode;
  hexImage?: React.ReactNode;
  header: LayoutHeaders;
  hideLayout?: boolean;
  // Severity of the banner on screen; the enroll header projects it onto its glyph.
  bannerStatus?: NOTIF_BANNER_TYPE;
}

// Keyed by `src` at its callsite so a new logo URL mounts a fresh instance —
// `loaded` must start false again rather than carry over from whatever the
// previous logo's `onLoad` left it at.
const TenantLogo = ({ src }: { src: string }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={src}
      alt="logo"
      className={`card-header-logo${loaded ? ' card-header-logo-loaded' : ''}`}
      onLoad={() => setLoaded(true)}
    />
  );
};

export const AuthnLayout = ({
  children,
  logoUrl,
  hexImage,
  header,
  hideLayout,
  bannerStatus,
}: AuthnLayoutProps) => {
  const { t } = useTranslation();
  const notifications = useNotificationChannel();
  useEventListener(event =>
    notifications.publish(notificationFromAppEvent(event)),
  );
  // The banner floats at the top of this card, as it did when it was positioned
  // inside it, rather than at the top of the window.
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const loadLogo = useMemo(
    () =>
      typeof logoUrl === 'string' ? (
        <TenantLogo key={logoUrl} src={logoUrl} />
      ) : (
        logoUrl
      ),
    [logoUrl],
  );
  const offlineBanner = useMemo(() => {
    return offlineBannerMetaData(
      t('errors.offline'),
      t('errors.offline-sub-message'),
    );
  }, [t]);
  const renderHeader = useMemo(() => {
    switch (header) {
      case LayoutHeaders.AUTHENTICATION:
        return <AuthenticationHeader logoUrl={loadLogo} hexImage={hexImage} />;
      case LayoutHeaders.ENROLL_CONSENT:
        return (
          <EnrollmentHeader hexImage={hexImage} bannerStatus={bannerStatus} />
        );
      default:
        return null;
    }
  }, [header, loadLogo, hexImage, bannerStatus]);

  return (
    <div
      ref={setRoot}
      className={`authn-layout-root ${hideLayout && 'hide-layout'}`}
    >
      <NotificationSystem
        subscribe={notifications.subscribe}
        handleOffline={offlineBanner}
        dismissOnInteraction
        container={root}
      />
      {renderHeader}
      <div className="authn-layout-body">{children}</div>
      <div className="authn-layout-footer">
        <ImprivataLogo />
      </div>
    </div>
  );
};
